import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PaymentOptions {
  amount?: number;
  currency?: string;
  description?: string;
  planId?: string;
  planType?: string;
  priceId?: string;
  isSubscription?: boolean;
  billingPeriod?: 'monthly' | 'quarterly' | 'yearly';
  metadata?: Record<string, string>;
  customerId?: string;
  successUrl?: string;
  cancelUrl?: string;
}

interface PaymentResult {
  success: boolean;
  paymentId?: string;
  error?: string;
  transactionId?: string;
  amount?: number;
  customerId?: string;
  subscriptionId?: string;
  url?: string;
}

/**
 * Redirects the user through Stripe Checkout via our `create-checkout` edge function.
 * Requires `priceId` (Stripe Price ID) on the passed options.
 */
export const useStripePayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastPaymentResult, setLastPaymentResult] = useState<PaymentResult | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const redirectToCheckout = async (options: PaymentOptions): Promise<PaymentResult> => {
    setIsProcessing(true);
    setPaymentError(null);
    try {
      const priceId = options.priceId ?? options.planId;
      if (!priceId) throw new Error('A Stripe priceId is required for checkout');

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          priceId,
          mode: options.isSubscription ? 'subscription' : 'payment',
          successUrl: options.successUrl,
          cancelUrl: options.cancelUrl,
        },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL returned');

      window.location.href = data.url as string;
      const result: PaymentResult = { success: true, url: data.url };
      setLastPaymentResult(result);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Checkout failed';
      setPaymentError(msg);
      const result: PaymentResult = { success: false, error: msg };
      setLastPaymentResult(result);
      return result;
    } finally {
      setIsProcessing(false);
    }
  };

  const openBillingPortal = async (): Promise<PaymentResult> => {
    setIsProcessing(true);
    setPaymentError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {});
      if (error) throw error;
      if (!data?.url) throw new Error('No portal URL returned');
      window.location.href = data.url as string;
      return { success: true, url: data.url };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Portal failed';
      setPaymentError(msg);
      return { success: false, error: msg };
    } finally {
      setIsProcessing(false);
    }
  };

  // Back-compat shim for older components that expected an in-page payment flow.
  // We now always route through Stripe Checkout instead of PaymentIntents on the client.
  const processPayment = async (_paymentMethodId: string, options: PaymentOptions): Promise<PaymentResult> => {
    return redirectToCheckout(options);
  };

  return {
    isProcessing,
    lastPaymentResult,
    paymentError,
    processPayment,
    redirectToCheckout,
    openBillingPortal,
    lastTransaction: lastPaymentResult?.success
      ? {
          id: lastPaymentResult.transactionId || '',
          status: 'success',
          amount: lastPaymentResult.amount || 0,
          customerId: lastPaymentResult.customerId,
          subscriptionId: lastPaymentResult.subscriptionId,
        }
      : null,
  };
};
